import { useState, useEffect, useCallback } from 'react';
import { hotelApi } from '../../hotelApi';

const EMPTY_CAT = { name: '', displayOrder: 0, isActive: true };
const EMPTY_ITEM = {
  categoryId: '', name: '', description: '', price: '',
  isVeg: true, isAvailable: true, availableFrom: '', availableTo: ''
};

export default function HotelMenu({ role }) {
  const [tab, setTab]             = useState('categories');
  const [categories, setCategories] = useState([]);
  const [items, setItems]         = useState([]);
  const [catFilter, setCatFilter] = useState('');
  const [loading, setLoading]     = useState(true);
  const [showCatForm, setShowCatForm] = useState(false);
  const [showItemForm, setShowItemForm] = useState(false);
  const [catForm, setCatForm]     = useState(EMPTY_CAT);
  const [itemForm, setItemForm]   = useState(EMPTY_ITEM);
  const [editCatId, setEditCatId] = useState(null);
  const [editItemId, setEditItemId] = useState(null);
  const [saving, setSaving]       = useState(false);
  const [err, setErr]             = useState('');

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([hotelApi.getCategories(), hotelApi.getMenuItems()])
      .then(([cats, its]) => { setCategories((cats || []).slice().sort((a, b) => a.name.localeCompare(b.name))); setItems(its || []); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const fmtRs = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

  // ── Categories ──────────────────────────────────────────────────────────────

  const saveCat = async () => {
    if (!catForm.name.trim()) { setErr('Name is required'); return; }
    setSaving(true); setErr('');
    try {
      if (editCatId) await hotelApi.updateCategory(editCatId, catForm);
      else await hotelApi.createCategory(catForm);
      setCatForm(EMPTY_CAT); setEditCatId(null); setShowCatForm(false); load();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const deleteCat = async (id, name) => {
    if (!window.confirm(`Delete category "${name}"?`)) return;
    await hotelApi.deleteCategory(id); load();
  };

  // ── Items ───────────────────────────────────────────────────────────────────

  const saveItem = async () => {
    if (!itemForm.name.trim()) { setErr('Name is required'); return; }
    if (!itemForm.price) { setErr('Price is required'); return; }
    setSaving(true); setErr('');
    try {
      if (editItemId) await hotelApi.updateMenuItem(editItemId, itemForm);
      else await hotelApi.createMenuItem(itemForm);
      setItemForm(EMPTY_ITEM); setEditItemId(null); setShowItemForm(false); load();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const toggleItem = async (id) => {
    await hotelApi.toggleMenuItem(id); load();
  };

  const deleteItem = async (id, name) => {
    if (!window.confirm(`Delete item "${name}"?`)) return;
    await hotelApi.deleteMenuItem(id); load();
  };

  const filteredItems = catFilter ? items.filter(i => String(i.category_id) === catFilter) : items;

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading menu…</div>;

  return (
    <div>
      <div className="tabs" style={{ marginBottom: 16 }}>
        <button className={`tab${tab === 'categories' ? ' active' : ''}`} onClick={() => setTab('categories')}>
          📂 Categories ({categories.length})
        </button>
        <button className={`tab${tab === 'items' ? ' active' : ''}`} onClick={() => setTab('items')}>
          📋 Items ({items.length})
        </button>
      </div>

      {/* ── Categories Tab ─────────────────────────────────────────── */}
      {tab === 'categories' && (
        <div>
          <div className="card">
            <div className="card-header">
              <div className="card-title">Menu Categories</div>
              <button className="btn btn-primary" style={{ fontSize: 13, padding: '5px 14px' }}
                onClick={() => { setCatForm(EMPTY_CAT); setEditCatId(null); setShowCatForm(true); }}>
                ➕ Add Category
              </button>
            </div>
            {showCatForm && (
              <div className="card-body" style={{ borderBottom: '1px solid #e8eaed', background: '#f8faf8' }}>
                {err && <div style={{ color: '#c5221f', marginBottom: 10, fontSize: 13 }}>⚠️ {err}</div>}
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div className="form-group" style={{ flex: 2, minWidth: 180 }}>
                    <label className="form-label">Category Name *</label>
                    <input className="form-control" value={catForm.name}
                      onChange={e => setCatForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="e.g. Starters, Main Course" />
                  </div>
                  <div className="form-group" style={{ flex: 1, minWidth: 100 }}>
                    <label className="form-label">Display Order</label>
                    <input className="form-control" type="number" value={catForm.displayOrder}
                      onChange={e => setCatForm(f => ({ ...f, displayOrder: e.target.value }))} />
                  </div>
                  <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 4 }}>
                    <input type="checkbox" id="catActive" checked={catForm.isActive}
                      onChange={e => setCatForm(f => ({ ...f, isActive: e.target.checked }))} />
                    <label htmlFor="catActive" className="form-label" style={{ margin: 0 }}>Active</label>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-primary" onClick={saveCat} disabled={saving}
                      style={{ padding: '7px 16px' }}>
                      {saving ? 'Saving…' : editCatId ? '💾 Update' : '➕ Add'}
                    </button>
                    <button className="btn btn-secondary" style={{ padding: '7px 14px' }}
                      onClick={() => { setShowCatForm(false); setEditCatId(null); setCatForm(EMPTY_CAT); setErr(''); }}>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr><th>Name</th><th>Order</th><th>Status</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {categories.length === 0 && (
                    <tr><td colSpan={4} style={{ textAlign: 'center', color: '#888', padding: 24 }}>No categories yet</td></tr>
                  )}
                  {categories.map(c => (
                    <tr key={c.id}>
                      <td><strong>{c.name}</strong></td>
                      <td>{c.display_order}</td>
                      <td>
                        <span style={{ background: c.is_active ? '#e6f4ea' : '#f1f3f4',
                          color: c.is_active ? '#137333' : '#888', borderRadius: 10,
                          padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>
                          {c.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <button className="btn-sm btn-secondary" style={{ marginRight: 4 }}
                          onClick={() => { setCatForm({ name: c.name, displayOrder: c.display_order, isActive: !!c.is_active }); setEditCatId(c.id); setShowCatForm(true); }}>
                          Edit
                        </button>
                        <button className="btn-sm btn-danger" onClick={() => deleteCat(c.id, c.name)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Items Tab ──────────────────────────────────────────────── */}
      {tab === 'items' && (
        <div>
          <div className="card">
            <div className="card-header" style={{ flexWrap: 'wrap', gap: 8 }}>
              <div className="card-title">Menu Items</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
                  className="form-control" style={{ minWidth: 160, fontSize: 13 }}>
                  <option value="">All Categories</option>
                  {categories.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
                </select>
                <button className="btn btn-primary" style={{ fontSize: 13, padding: '5px 14px' }}
                  onClick={() => { setItemForm(EMPTY_ITEM); setEditItemId(null); setShowItemForm(true); }}>
                  ➕ Add Item
                </button>
              </div>
            </div>

            {showItemForm && (
              <div className="card-body" style={{ borderBottom: '1px solid #e8eaed', background: '#f8faf8' }}>
                {err && <div style={{ color: '#c5221f', marginBottom: 10, fontSize: 13 }}>⚠️ {err}</div>}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                  <div className="form-group">
                    <label className="form-label">Name *</label>
                    <input className="form-control" value={itemForm.name}
                      onChange={e => setItemForm(f => ({ ...f, name: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Category</label>
                    <select className="form-control" value={itemForm.categoryId}
                      onChange={e => setItemForm(f => ({ ...f, categoryId: e.target.value }))}>
                      <option value="">— Select —</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Price (₹) *</label>
                    <input className="form-control" type="number" value={itemForm.price}
                      onChange={e => setItemForm(f => ({ ...f, price: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Available From</label>
                    <input className="form-control" type="time" value={itemForm.availableFrom}
                      onChange={e => setItemForm(f => ({ ...f, availableFrom: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Available To</label>
                    <input className="form-control" type="time" value={itemForm.availableTo}
                      onChange={e => setItemForm(f => ({ ...f, availableTo: e.target.value }))} />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">Description</label>
                    <input className="form-control" value={itemForm.description}
                      onChange={e => setItemForm(f => ({ ...f, description: e.target.value }))} />
                  </div>
                  <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                    <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
                      <input type="checkbox" checked={itemForm.isVeg}
                        onChange={e => setItemForm(f => ({ ...f, isVeg: e.target.checked }))} />
                      <span style={{ fontSize: 14 }}>🟢 Vegetarian</span>
                    </label>
                    <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
                      <input type="checkbox" checked={itemForm.isAvailable}
                        onChange={e => setItemForm(f => ({ ...f, isAvailable: e.target.checked }))} />
                      <span style={{ fontSize: 14 }}>Available</span>
                    </label>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                  <button className="btn btn-primary" onClick={saveItem} disabled={saving}>
                    {saving ? 'Saving…' : editItemId ? '💾 Update' : '➕ Add Item'}
                  </button>
                  <button className="btn btn-secondary"
                    onClick={() => { setShowItemForm(false); setEditItemId(null); setItemForm(EMPTY_ITEM); setErr(''); }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th><th>Category</th><th>Price</th><th>Type</th>
                    <th>Time Window</th><th>Status</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', color: '#888', padding: 24 }}>No items</td></tr>
                  )}
                  {filteredItems.map(item => (
                    <tr key={item.id}>
                      <td><strong>{item.name}</strong>
                        {item.description && <div style={{ fontSize: 11, color: '#888' }}>{item.description}</div>}
                      </td>
                      <td style={{ fontSize: 13, color: '#5f6368' }}>{item.category_name || '—'}</td>
                      <td style={{ fontWeight: 700, color: '#137333' }}>{fmtRs(item.price)}</td>
                      <td>
                        <span style={{ fontSize: 14 }}>{item.is_veg ? '🟢' : '🔴'}</span>
                        <span style={{ fontSize: 11, marginLeft: 4, color: '#888' }}>{item.is_veg ? 'Veg' : 'Non-Veg'}</span>
                      </td>
                      <td style={{ fontSize: 12, color: '#888' }}>
                        {item.available_from && item.available_to
                          ? `${item.available_from} – ${item.available_to}`
                          : 'All day'}
                      </td>
                      <td>
                        <button onClick={() => toggleItem(item.id)}
                          style={{ background: item.is_available ? '#e6f4ea' : '#fce8e6',
                            color: item.is_available ? '#137333' : '#c5221f',
                            border: 'none', borderRadius: 20, padding: '3px 10px',
                            cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>
                          {item.is_available ? '🟢 Available' : '🔴 Unavailable'}
                        </button>
                      </td>
                      <td>
                        <button className="btn-sm btn-secondary" style={{ marginRight: 4 }}
                          onClick={() => {
                            setItemForm({
                              categoryId: item.category_id || '',
                              name: item.name, description: item.description || '',
                              price: item.price, isVeg: !!item.is_veg,
                              isAvailable: !!item.is_available,
                              availableFrom: item.available_from || '',
                              availableTo: item.available_to || '',
                            });
                            setEditItemId(item.id); setShowItemForm(true);
                          }}>Edit</button>
                        <button className="btn-sm btn-danger" onClick={() => deleteItem(item.id, item.name)}>Del</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
